/**
 * The doors. Which ways into RecordOS this server can actually open, and what
 * the phone is allowed to draw because of it.
 *
 * **A door is not a button.** `AUTH_DOORS` is the closed vocabulary; whether a
 * door appears on a screen is decided twice, and both answers must be yes:
 *
 *   1. **The server can serve it.** A social door needs its client id and
 *      secret; the phone door needs an SMS or WhatsApp vendor; the email code
 *      needs a mail transport. None of those are in the build, so the server
 *      answers `GET /v1/auth/doors` from its own env at boot and the phone
 *      draws what it is told. A door whose secrets are absent is *absent*, not
 *      disabled: a button that cannot work is worse than no button.
 *   2. **The device can open it.** Apple's sheet is iOS, Truecaller's is an
 *      Android app that may not be installed. That half is the device's and
 *      lives in `apps/mobile/src/lib/doors.ts`; the server never guesses it.
 *
 * The order the doors are drawn in is the design's argument, not the server's,
 * and it lives on the device beside the platform half. See the Matrix board of
 * `design/explorations/signup-options`.
 */

/**
 * Every way in the product can offer. Closed, and ordered as the Map board
 * lists them — the two typed doors first, then the four vendor sheets.
 */
export const AUTH_DOORS = ['email', 'phone', 'apple', 'google', 'outlook', 'truecaller'] as const;
export type AuthDoor = (typeof AUTH_DOORS)[number];

/**
 * The four that are an OAuth provider on the server. `truecaller` is not one of
 * them — its sheet is native and its payload is verified by a plugin endpoint
 * of ours, which is the whole reason it needs a native module. See the Map
 * board's last row.
 */
export const SOCIAL_DOORS = ['apple', 'google', 'outlook'] as const;
export type SocialDoor = (typeof SOCIAL_DOORS)[number];

export const isSocialDoor = (door: AuthDoor): door is SocialDoor =>
  (SOCIAL_DOORS as readonly string[]).includes(door);

/**
 * Better Auth's own provider id for a social door.
 *
 * `outlook` is the word on the button because it is the word people know; the
 * provider is Microsoft, at `tenantId: 'common'` so a personal account is
 * admitted as well as a work one. The two names are kept apart here so the
 * screen never has to know the provider's, and the server never has to know
 * the button's.
 */
export const PROVIDER_FOR_DOOR: Readonly<Record<SocialDoor, string>> = {
  apple: 'apple',
  google: 'google',
  outlook: 'microsoft',
};

/** How a six-digit code reaches a phone. There is no third channel. */
export const OTP_CHANNELS = ['sms', 'whatsapp'] as const;
export type OtpChannel = (typeof OTP_CHANNELS)[number];

/**
 * Where one channel can deliver, in three states that a two-state field could
 * not tell apart:
 *
 *   `null`      no vendor. The channel is off everywhere.
 *   `'all'`     a vendor, and no country list. Everywhere it reaches.
 *   `string[]`  a vendor, and an allow-list of ISO 3166-1 alpha-2 codes.
 *               An empty array is a vendor that is allowed nowhere.
 *
 * The distinction between the first two is what decides whether the phone door
 * is hidden or drawn, so it cannot be collapsed. CashBook reads its allow-list
 * from remote config; the Blockers board records that RecordOS has no source
 * for one yet, which is why `'all'` is a legal answer rather than a stand-in.
 */
export type ChannelWhere = readonly string[] | 'all' | null;

export type ChannelReach = {
  readonly sms: ChannelWhere;
  readonly whatsapp: ChannelWhere;
};

/** Nothing configured. What `GET /v1/auth/doors` answers on a bare checkout. */
export const NO_REACH: ChannelReach = { sms: null, whatsapp: null };

/**
 * GET /v1/auth/doors
 *
 * Unauthenticated, because it is read by a person who has no session yet. It
 * says what this deployment can do and never anything about a person.
 */
export type AuthDoorsResponse = {
  /** The doors whose server side is configured. The order here means nothing. */
  readonly doors: readonly AuthDoor[];
  /** Where a code can land. Off on both sides hides the phone door. */
  readonly reach: ChannelReach;
  /**
   * Whether an email code can actually leave the building: true when the
   * server sends through SES, false where it only logs the email (a laptop,
   * the e2e suite). The email door is the password form either way today.
   */
  readonly emailCodeDelivers: boolean;
};

/** Off is never reachable; `'all'` always is; a list is asked. */
const reaches = (where: ChannelWhere, iso: string): boolean =>
  where === null ? false : where === 'all' ? true : where.includes(iso.toUpperCase());

/**
 * The channels that can carry a code to this country, in the order to offer
 * them. SMS leads where it works, because it needs no second app.
 *
 * An empty result is the instruction to hide the phone door for that country
 * rather than to draw it and fail — the Matrix board's "no SMS, no WhatsApp"
 * row. One channel and it is WhatsApp is the `phone.whatsappOnly` case.
 */
export const channelsFor = (reach: ChannelReach, iso: string): readonly OtpChannel[] => {
  const found: OtpChannel[] = [];
  if (reaches(reach.sms, iso)) found.push('sms');
  if (reaches(reach.whatsapp, iso)) found.push('whatsapp');
  return found;
};
